// Smart Sash Admin Panel - Authentication Service
// Handles login, logout, and user info

import {
  AuthUser,
  CurrentUserInfo,
  LoginByCardRequest,
  LoginByCardResponse,
  LoginRequest,
  LoginResponse,
  VerifyUserRequest,
  parsePipeSeparatedIds,
} from '@/models';
import { axiosClient } from '../http/axiosClient';

/**
 * Authentication API endpoints
 */
const AUTH_ENDPOINTS = {
  LOGIN: '/Auth/login',
  LOGIN_BY_CARD: '/Auth/login-by-card',
  LOGOUT: '/Auth/logout',
  REFRESH: '/Auth/refresh',
  REVOKE: '/Auth/revoke',
  REVOKE_ALL: '/Auth/revoke-all',
  VERIFY: '/Auth/verify',
  ME: '/Auth/me',
} as const;

/**
 * Authentication Service
 */
export const authService = {
  /**
   * Login with username or email.
   * The server sets HttpOnly auth cookies — no tokens are returned in the body.
   * A 301 response means the user must change their password before normal access.
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await axiosClient.post(
      AUTH_ENDPOINTS.LOGIN,
      {
        email: credentials.login?.trim() || credentials.email?.trim() || '',
        password: credentials.password,
      },
      {
        validateStatus: (status) => status === 200 || status === 301,
      }
    );

    return {
      requiresPasswordChange: response.status === 301,
    };
  },

  /**
   * Login by RFID card
   */
  loginByCard: async (data: LoginByCardRequest): Promise<LoginByCardResponse> => {
    const response = await axiosClient.post<LoginByCardResponse>(
      AUTH_ENDPOINTS.LOGIN_BY_CARD,
      data
    );
    return response.data;
  },

  /**
   * Logout current user — the server clears the auth cookies.
   */
  logout: async (): Promise<void> => {
    await axiosClient.post(AUTH_ENDPOINTS.LOGOUT);
  },

  /**
   * Revoke a specific refresh token
   */
  revoke: async (refreshToken: string): Promise<void> => {
    await axiosClient.post(AUTH_ENDPOINTS.REVOKE, { refreshToken });
  },

  /**
   * Revoke all refresh tokens for current user
   */
  revokeAll: async (): Promise<void> => {
    await axiosClient.post(AUTH_ENDPOINTS.REVOKE_ALL);
  },

  /**
   * Verify user email with code
   */
  verifyUser: async (request: VerifyUserRequest): Promise<void> => {
    await axiosClient.post(AUTH_ENDPOINTS.VERIFY, request);
  },

  /**
   * Get current authenticated user info.
   * Used on app load and after login to restore/confirm the session.
   * Interceptor already unwraps {success, data, error} envelope.
   */
  getCurrentUser: async (): Promise<AuthUser> => {
    const response = await axiosClient.get<CurrentUserInfo>(AUTH_ENDPOINTS.ME);
    const data = response.data;

    return {
      id: data.userId,
      email: data.email || '',
      firstName: data.firstName || undefined,
      lastName: data.lastName || undefined,
      institutionId: data.institutionId,
      institutionName: data.institutionName || undefined,
      roles: [],
      permissions: data.permissions ?? [],
      assignedLabIds: parsePipeSeparatedIds(undefined),
      assignedDeviceIds: parsePipeSeparatedIds(undefined),
    };
  },
};

export default authService;
